import { GetStaticProps } from 'next';
import { PageInformationFieldsFragment, ProjectOverviewSingleFieldsFragment, Theme } from 'api/graphql-types';
import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetProjectOverview } from 'api/graphql-helper';
import { Head } from 'modules/head/head';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { initialState as projectsInitialState } from 'redux/stores/projects';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import React from 'react';
import IntroHeader from 'modules/intro-header/intro-header';
import { ROUTES } from 'constants/routes';
import { IBasePageProps } from 'types/page';
import { searchProjects } from 'api/algolia';
import { PROJECTS_OVERVIEW_LIMIT } from 'modules/projects-overview/data';

interface IProjectOverviewPageProps extends IBasePageProps {
	projectOverview?: ProjectOverviewSingleFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
}

const ProjectOverviewPage = (props: IProjectOverviewPageProps): React.JSX.Element => {
	const modules = props.projectOverview?.modules || [];
	const seoModule = props?.projectOverview?.seo;
	const modulesWithDefaultModuleOptions = modules?.map((module: any) => {
		if (module?.__typename === 'ComponentModulesProjectOverview') {
			return {
				...module,
				defaultModuleOptions: module.extendedDefaultModuleOptions
			};
		}
		return { ...module };
	});

	return (
		<Layout navbar={props?.pageInformation?.navbar} footer={props?.pageInformation?.footer}>
			<Head seoModule={seoModule} seoParent={props?.pageInformation?.seo} />
			<IntroHeader module={props?.projectOverview?.header} slug={ROUTES.PROJECTS.fullPath} />
			<ModuleBuilder modules={modulesWithDefaultModuleOptions} slug={ROUTES.PROJECTS.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps<IProjectOverviewPageProps> = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetProjectOverview({ variables: { locale, publicationState } }, client);
		const { pageInformation, projectOverview, themes_connection: themes } = result.props.data;

		const { results: projects, total: projectsCount } = await searchProjects(
			'',
			{
				themesIds: []
			},
			{
				sort: 'asc',
				page: 1,
				limit: PROJECTS_OVERVIEW_LIMIT,
				locale: locale
			}
		);

		if (projectOverview == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				projectOverview: projectOverview?.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'project-overview',
						contentTypeId: projectOverview?.data?.documentId
					},
					projects: {
						...projectsInitialState,
						projects,
						projectsCount
					},
					themes: {
						themes: (themes?.data as Theme[]) || [],
						totalThemes: themes?.pageInfo.total || 0
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in project overview', error);

		return {
			notFound: true
		};
	}
};

export default ProjectOverviewPage;
